import React, { useEffect, useRef } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { store } from "./store";
import AppRoutes from "./routes";
import { OfflineBanner } from "./components/app/OfflineBanner";
import SessionExpiredModal from "./components/app/SessionExpiredModal";
import { SyncStatusIndicator } from "./components/app/SyncComponents";
import {
  registerServiceWorker,
  shouldEnablePWA,
} from "./utils/serviceWorkerRegistration";
import { initializeSyncEngine } from "./utils/syncEngine";

// MULTI-TENANT: Generate tenant-specific manifest dynamically
const generateTenantManifest = () => {
  const origin = window.location.origin;
  const hostname = window.location.hostname;
  const tenantId = hostname.split(".")[0]; // e.g., "acme" from "acme.livecargo.ee"

  const manifest = {
    name: `LiveCargo Driver - ${tenantId}`,
    short_name: "LiveCargo",
    description: "Logistics platform for drivers",
    start_url: `${origin}/`, // Tenant-specific origin
    scope: `${origin}/`,
    display: "standalone",
    background_color: "#ffffff",
    theme_color: "#2563eb",
    orientation: "portrait-primary",
    icons: [
      {
        src: `${origin}/favicon-16x16.png`,
        sizes: "16x16",
        type: "image/png",
        purpose: "any maskable",
      },
      {
        src: `${origin}/favicon-32x32.png`,
        sizes: "32x32",
        type: "image/png",
        purpose: "any maskable",
      },
      {
        src: `${origin}/android-chrome-192x192.png`,
        sizes: "192x192",
        type: "image/png",
        purpose: "any maskable",
      },
      {
        src: `${origin}/android-chrome-512x512.png`,
        sizes: "512x512",
        type: "image/png",
        purpose: "any maskable",
      },
    ],
    categories: ["business", "logistics"],
  };

  // Create blob URL for manifest
  const manifestBlob = new Blob([JSON.stringify(manifest)], {
    type: "application/json",
  });

  return URL.createObjectURL(manifestBlob);
};

const PwaLifecycle: React.FC = () => {
  const location = useLocation();
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!shouldEnablePWA()) return;

    // DYNAMIC MANIFEST: Inject tenant-specific manifest
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.remove();
    }

    const manifestLink = document.createElement("link");
    manifestLink.rel = "manifest";
    manifestLink.href = generateTenantManifest(); // Dynamically generated
    document.head.appendChild(manifestLink);

    // console.log(
    //   "[PWA] Dynamic manifest injected for:",
    //   window.location.hostname,
    // );

    // Register service worker
    registerServiceWorker({
      onSuccess: () => {
        // console.log("[PWA] Service worker registered successfully");
      },
      onUpdate: (registration) => {
        // console.log("[PWA] New version available");
        if (window.confirm("A new version is available. Reload to update?")) {
          if (registration.waiting) {
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
            window.location.reload();
          }
        }
      },
    });

    // Initialize sync engine once
    if (!hasInitializedRef.current) {
      initializeSyncEngine();
      hasInitializedRef.current = true;
    }

    // Add theme color meta tag
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
      const meta = document.createElement("meta");
      meta.name = "theme-color";
      meta.content = "#2563eb";
      document.head.appendChild(meta);
    }
  }, [location.pathname]);

  return null;
};

// App Content Component
const AppContent: React.FC = () => {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <PwaLifecycle />
        {/* Show offline banner on driver pages */}
        {shouldEnablePWA() && <OfflineBanner />}

        <AppRoutes />

        {/* Show sync status indicator on driver pages */}
        {shouldEnablePWA() && <SyncStatusIndicator />}

        {/* Single-session: this device was signed out by a login elsewhere */}
        <SessionExpiredModal />
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;
